import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/lib/sections";

const EDITORIAL_FILTER = {
  filter: "contrast(1.04) brightness(0.96) saturate(0.92)",
};

const FRAME_DECOR = (
  <>
    <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-gold/30" />
    <div className="pointer-events-none absolute top-3 left-3 h-5 w-5 border-t border-l border-gold/60" />
    <div className="pointer-events-none absolute top-3 right-3 h-5 w-5 border-t border-r border-gold/60" />
    <div className="pointer-events-none absolute bottom-3 left-3 h-5 w-5 border-b border-l border-gold/60" />
    <div className="pointer-events-none absolute bottom-3 right-3 h-5 w-5 border-b border-r border-gold/60" />
  </>
);

const StoreFrame = ({ src, alt, label, location, aspect = "aspect-[4/3]", delay = 0 }) => (
  <Reveal delay={delay} className="w-full">
    <div className={`relative ${aspect} w-full overflow-hidden`}>
      <div className="absolute inset-0 bg-coal" />
      <img
        src={src}
        alt={alt}
        crossOrigin="anonymous"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-luxe hover:scale-[1.04]"
        style={EDITORIAL_FILTER}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-2/5 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, rgba(17,17,17,0.6) 55%, rgba(17,17,17,0.95) 100%)",
        }}
      />
      {FRAME_DECOR}
      <div className="absolute bottom-5 left-5 right-5 z-10">
        <div className="num-overline text-gold/85">{label}</div>
        <div className="mt-1 font-heading italic text-ivory text-lg md:text-xl">
          {location}
        </div>
      </div>
    </div>
  </Reveal>
);

export default function RetailDisplay() {
  return (
    <section
      id="display"
      data-testid="section-display"
      className="deck-section relative bg-ink"
    >
      <div className="mx-auto w-full max-w-[1600px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-7">
            <div className="num-overline">08 · On The Floor</div>
            <h2 className="mt-8 font-heading text-ivory text-4xl md:text-5xl lg:text-[60px] leading-[1.05] tracking-tightest">
              How Aarya24KT
              <br />
              <span className="italic text-gold">lives</span> in retail.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="font-body text-beige/75 text-[15px] leading-[1.85] max-w-md">
              A glimpse of how Aarya24KT is presented across premium jewellery
              destinations in India — a complete shop-in-shop experience that
              elevates the entire retail floor.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <div className="lg:col-span-7">
            <StoreFrame
              src={IMAGES.displayStore}
              alt="Aarya24KT shop-in-shop interior with chandelier and gold foil wall display"
              label="Shop-in-Shop Experience"
              location="Premium Jewellery Retailer · India"
              aspect="aspect-[4/3]"
              delay={0.1}
            />
          </div>
          <div className="lg:col-span-5">
            <StoreFrame
              src={IMAGES.displayWall}
              alt="Wall display of Aarya24KT gold foil art collection"
              label="Curated Wall Display"
              location="Festive Merchandising · India"
              aspect="aspect-[4/3]"
              delay={0.25}
            />
          </div>
        </div>

        {/* Bottom strip — display capabilities */}
        <Reveal delay={0.5}>
          <div className="mt-12 md:mt-16 grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-0 md:divide-x divide-gold/15 border-t border-gold/15 pt-8">
            {[
              "Shop-in-Shop",
              "Counter Displays",
              "Shelf Styling",
              "Festive Merchandising",
              "Premium Packaging",
            ].map((d, i) => (
              <div key={i} className="md:px-6">
                <div className="num-overline text-gold/70">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-2 font-heading text-ivory text-base md:text-lg leading-snug">
                  {d}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
